/**
 * Automation rules (TRUS-686, system_events_contract_v2.md section E):
 * org-level "When <event> [and conditions] then <actions>", no member needed.
 *
 *   GET    /api/workflows/automation-rules
 *   POST   /api/workflows/automation-rules
 *   GET    /api/workflows/automation-rules/{id}
 *   PUT    /api/workflows/automation-rules/{id}
 *   DELETE /api/workflows/automation-rules/{id}
 *   POST   /api/workflows/automation-rules/{id}/test   (dry run, synthetic event)
 *   GET    /api/workflows/automation-rules/{id}/runs?limit=
 *
 * Org admins only (403 `admin_only`); platform-event triggers only for
 * platform admins in the operator org. Parsing is defensive: the pre-TRUS-686
 * rule shape (`rule_name`, `trigger_type`, `trigger_conditions`) still reads,
 * and anything malformed reads as "nothing" rather than a crash.
 */

import { apiClient } from './apiClient';
import { errorFrom, parseFilters, serializeFilters, type SystemEventFilters } from './systemEventsApi';

export const AUTOMATION_RULES_BASE = '/api/workflows/automation-rules';

export type AutomationActionType = 'post_to_channel' | 'notify_staff' | 'create_task' | 'send_email';
export const AUTOMATION_ACTION_TYPES: readonly AutomationActionType[] = [
  'post_to_channel',
  'notify_staff',
  'create_task',
  'send_email',
];

export interface StaffRecipientsInput {
  assignee?: boolean;
  case_manager?: boolean;
  roles?: string[];
  user_ids?: number[];
}

export interface PostToChannelAction {
  type: 'post_to_channel';
  channel_id: number | null;
  message_template?: string;
}

export interface NotifyStaffAction {
  type: 'notify_staff';
  recipients: StaffRecipientsInput;
  channels: Array<'bell' | 'push'>;
  message_template?: string;
}

/** Who a created task goes to: one person, the event's assignee, or a role. */
export type TaskAssignee = { user_id: number } | { assignee_of_event: true } | { role: string };

export interface CreateTaskAction {
  type: 'create_task';
  title_template: string;
  assignee: TaskAssignee;
  due_in_days?: number | null;
}

export interface SendEmailAction {
  type: 'send_email';
  to_user_ids?: number[];
  roles?: string[];
  subject_template: string;
  body_template: string;
}

export type AutomationAction = PostToChannelAction | NotifyStaffAction | CreateTaskAction | SendEmailAction;

export interface AutomationTrigger {
  event_type: string;
  conditions: SystemEventFilters;
}

export interface AutomationRule {
  id: number;
  name: string;
  is_active: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  created_by: string | null;
  updated_at: string | null;
}

export interface AutomationRuleInput {
  name: string;
  is_active: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
}

export interface AutomationTestAction {
  type: string;
  status: string | null;
  text: string;
  /** Why this action would not run (contract: actions[].skipped_reason). */
  skippedReason: string | null;
}

export interface AutomationTestResult {
  /** False when the synthetic event would not match the rule's conditions. */
  matched: boolean | null;
  /** contract: conditions_matched (null when not sent). */
  conditionsMatched: boolean | null;
  summary: string | null;
  actions: AutomationTestAction[];
  raw: unknown;
}

export interface AutomationRun {
  id: number;
  system_event_id: number | null;
  event_type: string | null;
  status: 'ok' | 'failed' | 'skipped' | string;
  error: string | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const posInt = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};
const ids = (v: unknown): number[] => (Array.isArray(v) ? v.map(posInt).filter((n): n is number => n !== null) : []);
const strs = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : [];

function parseRecipients(v: any): StaffRecipientsInput {
  const r = v && typeof v === 'object' ? v : {};
  return {
    assignee: r.assignee === true,
    case_manager: r.case_manager === true,
    roles: strs(r.roles),
    user_ids: ids(r.user_ids),
  };
}

function parseAssignee(v: any): TaskAssignee {
  if (v && typeof v === 'object') {
    const uid = posInt(v.user_id);
    if (uid) return { user_id: uid };
    if (text(v.role)) return { role: v.role };
  }
  return { assignee_of_event: true };
}

export function parseAction(raw: any): AutomationAction | null {
  // The old shape nested settings under `config`.
  const a = raw && typeof raw === 'object' ? { ...(raw.config || {}), ...raw } : null;
  if (!a) return null;
  switch (a.type) {
    case 'post_to_channel':
      return { type: 'post_to_channel', channel_id: posInt(a.channel_id), message_template: text(a.message_template) ?? '' };
    case 'notify_staff':
      return {
        type: 'notify_staff',
        recipients: parseRecipients(a.recipients),
        channels: Array.isArray(a.channels) ? (['bell', 'push'] as const).filter((c) => a.channels.includes(c)) : ['bell'],
        message_template: text(a.message_template) ?? '',
      };
    case 'create_task': {
      const due = Number(a.due_in_days);
      return {
        type: 'create_task',
        title_template: text(a.title_template) ?? '',
        assignee: parseAssignee(a.assignee),
        due_in_days: Number.isFinite(due) && due >= 0 ? due : null,
      };
    }
    case 'send_email':
      return {
        type: 'send_email',
        to_user_ids: ids(a.to_user_ids),
        roles: strs(a.roles),
        subject_template: text(a.subject_template) ?? '',
        body_template: text(a.body_template) ?? '',
      };
    default:
      return null;
  }
}

export function parseRule(raw: any): AutomationRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = posInt(raw.id);
  if (!id) return null;
  const trigger = raw.trigger && typeof raw.trigger === 'object' ? raw.trigger : {};
  const by = raw.created_by;
  return {
    id,
    name: text(raw.name) ?? text(raw.rule_name) ?? `#${id}`,
    is_active: raw.is_active !== false,
    trigger: {
      event_type: text(trigger.event_type) ?? text(raw.trigger_type) ?? '',
      conditions: parseFilters(trigger.conditions ?? raw.trigger_conditions),
    },
    actions: (Array.isArray(raw.actions) ? raw.actions : []).map(parseAction).filter((x: AutomationAction | null): x is AutomationAction => !!x),
    created_by: text(by) ?? text(by?.name) ?? null,
    updated_at: text(raw.updated_at) ?? text(raw.last_updated_ts) ?? null,
  };
}

export function parseRules(data: unknown): AutomationRule[] {
  const list = Array.isArray(data) ? data : (data as any)?.rules;
  if (!Array.isArray(list)) return [];
  return list.map(parseRule).filter((r): r is AutomationRule => !!r);
}

export function parseTestResult(data: unknown): AutomationTestResult {
  const d: any = data && typeof data === 'object' ? data : {};
  const list = Array.isArray(d.actions) ? d.actions : Array.isArray(d.would_do) ? d.would_do : [];
  const matched =
    typeof d.matched === 'boolean' ? d.matched : typeof d.would_run === 'boolean' ? d.would_run : null;
  return {
    matched,
    conditionsMatched: typeof d.conditions_matched === 'boolean' ? d.conditions_matched : null,
    summary: text(d.summary) ?? text(d.message) ?? text(d.skipped_reason) ?? null,
    actions: list
      .filter((a: any) => a && typeof a === 'object')
      .map((a: any) => ({
        type: text(a.type) ?? 'action',
        status:
          text(a.status) ??
          (a.would_run === true ? 'would_run' : a.would_run === false || a.would === false ? 'skipped' : null),
        text: text(a.summary) ?? text(a.description) ?? text(a.message) ?? text(a.preview) ?? text(a.error) ?? '',
        skippedReason: text(a.skipped_reason) ?? text(a.reason) ?? null,
      })),
    raw: data,
  };
}

export function parseRuns(data: unknown): AutomationRun[] {
  const list = Array.isArray(data) ? data : (data as any)?.runs;
  if (!Array.isArray(list)) return [];
  return list
    .filter((r: any) => r && typeof r === 'object' && posInt(r.id))
    .map((r: any) => ({
      id: posInt(r.id)!,
      system_event_id: posInt(r.system_event_id),
      event_type: text(r.event_type),
      status: text(r.status) ?? 'ok',
      error: text(r.error),
      created_at: text(r.created_at),
    }));
}

/** The body the API takes (empty condition lists = all). */
export function serializeRule(input: AutomationRuleInput): Record<string, unknown> {
  return {
    name: input.name.trim(),
    is_active: input.is_active,
    trigger: { event_type: input.trigger.event_type, conditions: serializeFilters(input.trigger.conditions) },
    actions: input.actions.map((a) => {
      const out: Record<string, unknown> = { ...a };
      // Optional fields are left out rather than sent empty.
      if (out.message_template === '') delete out.message_template;
      if (out.due_in_days == null) delete out.due_in_days;
      if (Array.isArray(out.to_user_ids) && out.to_user_ids.length === 0) delete out.to_user_ids;
      if (Array.isArray(out.roles) && out.roles.length === 0) delete out.roles;
      return out;
    }),
  };
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

const rulePath = (id: number) => `${AUTOMATION_RULES_BASE}/${id}`;

async function call<T>(fn: () => Promise<{ data: unknown }>, parse: (d: unknown) => T): Promise<T> {
  try {
    const res = await fn();
    return parse(res.data);
  } catch (err) {
    throw errorFrom(err);
  }
}

export const automationRulesApi = {
  list(): Promise<AutomationRule[]> {
    return call(() => apiClient.get(AUTOMATION_RULES_BASE), parseRules);
  },
  get(id: number): Promise<AutomationRule | null> {
    return call(() => apiClient.get(rulePath(id)), (d) => parseRule((d as any)?.rule ?? d));
  },
  create(input: AutomationRuleInput): Promise<AutomationRule | null> {
    return call(() => apiClient.post(AUTOMATION_RULES_BASE, serializeRule(input)), (d) => parseRule((d as any)?.rule ?? d));
  },
  update(id: number, input: AutomationRuleInput): Promise<AutomationRule | null> {
    return call(() => apiClient.put(rulePath(id), serializeRule(input)), (d) => parseRule((d as any)?.rule ?? d));
  },
  remove(id: number): Promise<void> {
    return call(() => apiClient.delete(rulePath(id)), () => undefined);
  },
  test(id: number): Promise<AutomationTestResult> {
    return call(() => apiClient.post(`${rulePath(id)}/test`, {}), parseTestResult);
  },
  runs(id: number, limit = 50): Promise<AutomationRun[]> {
    return call(() => apiClient.get(`${rulePath(id)}/runs`, { params: { limit } }), parseRuns);
  },
};

export type AutomationRulesApi = typeof automationRulesApi;
